import React from 'react';
import PropTypes from 'prop-types';

import OutboundLink from 'components/OutboundLink';
import {
	SOURCE_BIS,
	SOURCE_DOBNOW,
} from 'data/repositories/certificatesOfOccupancyRepository';

/**
 * Why DOB NOW listed a certificate but produced no document for it.
 *
 * Mirrors the reason codes the scraper records in certificate_url_error, and
 * the wording of DobNowCertificateOfOccupancyController::explain() on the API
 * side, so a reader gets the same explanation wherever they meet it.
 */
const MISSING_DOCUMENT_REASONS = {
	print_control_absent:
		'DOB NOW offers no print control for this certificate, which usually means the document has not been published yet.',
	document_tab_never_opened:
		'DOB NOW accepted the print request but never returned a document.',
	not_a_pdf: 'DOB NOW returned something that was not a PDF.',
};

/** Shown when a certificate has no document and no recorded reason. */
const NOT_AVAILABLE_YET = 'Not available yet';

/**
 * Renders a date without inventing precision the value does not have.
 *
 * issued_date arrives as a plain YYYY-MM-DD and is shown as-is: parsing it into
 * a Date would apply the viewer's timezone and can move a certificate's issue
 * date across a day boundary, which on a legal document is worse than a plain
 * string.
 *
 * @param {?string} value
 * @returns {string}
 */
const formatDate = (value) => {
	if (!value) return '—';
	return String(value).slice(0, 10);
};

/**
 * The document cell: a link out to DOB's own copy, or why there isn't one.
 *
 * The URL is opened in a new tab rather than proxied through us. Both hosts sit
 * behind Akamai and refuse scripted clients, but serve a real browser — so the
 * viewer's own browser is the one client guaranteed to work.
 *
 * @param {object} props
 * @param {string} props.source
 * @param {object} props.row
 * @returns {JSX.Element}
 */
const DocumentCell = ({ source, row }) => {
	const url = source === SOURCE_BIS ? row.source_url : row.certificate_url;

	if (url) {
		return <OutboundLink url={url} label="View PDF" />;
	}

	const reason = row.certificate_url_error;

	if (reason) {
		return (
			<span className="text-muted" title={MISSING_DOCUMENT_REASONS[reason] || reason}>
				Unavailable <i className="bi bi-info-circle" />
			</span>
		);
	}

	return <span className="text-muted">{NOT_AVAILABLE_YET}</span>;
};

DocumentCell.propTypes = {
	source: PropTypes.oneOf([SOURCE_BIS, SOURCE_DOBNOW]).isRequired,
	row: PropTypes.object.isRequired,
};

/**
 * The BIS columns.
 *
 * One row is one downloadable PDF, not one certificate: a BIS certificate
 * number can carry several documents, so the same co_number can repeat.
 */
const BIS_COLUMNS = [
	{ label: 'CO Number', render: (row) => row.co_number || '—' },
	{ label: 'Document Name', render: (row) => row.document_name || '—' },
];

/** The DOB NOW columns. */
const DOBNOW_COLUMNS = [
	{ label: 'CO Number', render: (row) => row.co_number || '—' },
	{ label: 'Filing Type', render: (row) => row.filing_type || '—' },
	{ label: 'Status', render: (row) => row.status || '—' },
	{ label: 'Issued', render: (row) => formatDate(row.issued_date) },
	{ label: 'Job Number', render: (row) => row.job_number || '—' },
	{ label: 'Occupancy', render: (row) => row.occupancy_classification || '—' },
	{ label: 'Stories', render: (row) => row.stories || '—' },
];

/**
 * CertificatesOfOccupancyTable
 *
 * The certificates held for one property from one source. Presentational: it
 * renders what it is handed and fetches nothing.
 *
 * @param {object} props
 * @param {string} props.source Which source these rows came from.
 * @param {object[]} props.rows
 * @param {boolean} props.isLoading
 * @returns {JSX.Element}
 */
const CertificatesOfOccupancyTable = ({ source, rows, isLoading }) => {
	const columns = source === SOURCE_BIS ? BIS_COLUMNS : DOBNOW_COLUMNS;

	if (!isLoading && (!rows || rows.length === 0)) {
		return (
			<p className="cofo-empty text-muted">
				{source === SOURCE_BIS
					? 'No BIS certificates of occupancy are on file for this property. BIS holds certificates issued before roughly March 2021 — check the DOB NOW tab for newer ones.'
					: 'No DOB NOW certificates of occupancy are on file for this property. DOB NOW holds certificates from roughly 2021 onward — check the BIS tab for older ones.'}
			</p>
		);
	}

	return (
		<table className="table cofo-table">
			<thead>
				<tr>
					{columns.map((column) => (
						<th key={column.label} scope="col">
							{column.label}
						</th>
					))}
					<th scope="col">PDF</th>
				</tr>
			</thead>
			<tbody>
				{(rows || []).map((row) => (
					<tr key={`${row.id}`}>
						{columns.map((column) => (
							<td key={column.label}>{column.render(row)}</td>
						))}
						<td>
							<DocumentCell source={source} row={row} />
						</td>
					</tr>
				))}
			</tbody>
		</table>
	);
};

CertificatesOfOccupancyTable.propTypes = {
	source: PropTypes.oneOf([SOURCE_BIS, SOURCE_DOBNOW]).isRequired,
	rows: PropTypes.array,
	isLoading: PropTypes.bool,
};

CertificatesOfOccupancyTable.defaultProps = {
	rows: [],
	isLoading: false,
};

export default CertificatesOfOccupancyTable;
